import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown } from 'lucide-react';
import { DRVRequirements, NutrientRequirement } from '@/types/client';
import { NutrientCard, NutrientList } from './NutrientCard';
import { useState } from 'react';

interface MacroTargetsSectionProps {
  drvRequirements: DRVRequirements | null;
  macroTargets: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const MacroTargetsSection = ({
  drvRequirements,
  macroTargets,
  isOpen,
  onOpenChange
}: MacroTargetsSectionProps) => {
  const [carbohydratesOpen, setCarbohydratesOpen] = useState(true);
  const [lipidsOpen, setLipidsOpen] = useState(true);
  const [proteinsOpen, setProteinsOpen] = useState(true);
  const [waterOpen, setWaterOpen] = useState(true);

  const hasCarbohydrates = drvRequirements?.macronutrients?.carbohydrates && drvRequirements.macronutrients.carbohydrates.length > 0;
  const hasLipids = drvRequirements?.macronutrients?.lipids && drvRequirements.macronutrients.lipids.length > 0;
  const hasProteins = drvRequirements?.macronutrients?.proteins && drvRequirements.macronutrients.proteins.length > 0;
  const hasWater = drvRequirements?.other?.some(n => n.nutrientId === 'water' || n.nutrientId === 'water_from_drinks');
  const hasMacros = hasCarbohydrates || hasLipids || hasProteins || hasWater || macroTargets;

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="bg-white border-cs-secondary/30 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-cs-secondary to-cs-primary"></div>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/10 transition-colors">
            <CardTitle className="flex items-center justify-between text-gray-900">
              <span className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Macro Targets
                </span>
                {drvRequirements?.fallbackSourcesUsed && drvRequirements.fallbackSourcesUsed.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    Fallback DRV formulas used for specific fields:{" "}
                    {drvRequirements.fallbackSourcesUsed
                      .map((s) => s.replace(/_/g, " ").toUpperCase())
                      .join(", ")}
                  </span>
                )}
                <span className="text-xs font-normal text-gray-500">
                  Hover nutrient headings or values to see which formula each field uses.
                </span>
              </span>
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-200 text-cs-secondary ${isOpen ? 'rotate-180' : ''}`} 
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Carbohydrates */}
            {hasCarbohydrates && (
              <Collapsible open={carbohydratesOpen} onOpenChange={setCarbohydratesOpen}>
                <Card className="bg-white border-cs-secondary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Carbohydrates</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${carbohydratesOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.macronutrients.carbohydrates?.map((nutrient: NutrientRequirement) => (
                          <NutrientCard 
                            key={nutrient.nutrientId}
                            nutrient={nutrient}
                            borderColor="border-cs-secondary/20"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Lipids */}
            {hasLipids && (
              <Collapsible open={lipidsOpen} onOpenChange={setLipidsOpen}>
                <Card className="bg-white border-cs-secondary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Lipids</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${lipidsOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.macronutrients.lipids?.map((nutrient: NutrientRequirement) => (
                          <NutrientCard 
                            key={nutrient.nutrientId}
                            nutrient={nutrient}
                            borderColor="border-cs-secondary/20"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Proteins */}
            {hasProteins && (
              <Collapsible open={proteinsOpen} onOpenChange={setProteinsOpen}>
                <Card className="bg-white border-cs-secondary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Proteins</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${proteinsOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.macronutrients.proteins?.map((nutrient: NutrientRequirement) => (
                          <NutrientCard 
                            key={nutrient.nutrientId}
                            nutrient={nutrient}
                            borderColor="border-cs-secondary/20"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Water */}
            {hasWater && (
              <Collapsible open={waterOpen} onOpenChange={setWaterOpen}>
                <Card className="bg-white border-cs-secondary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Water</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${waterOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.other
                          ?.filter((nutrient) => nutrient.nutrientId === 'water' || nutrient.nutrientId === 'water_from_drinks')
                          .map((nutrient: NutrientRequirement) => (
                            <NutrientCard 
                              key={nutrient.nutrientId}
                              nutrient={nutrient}
                              borderColor="border-cs-secondary/20"
                            />
                          ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Fallback to old macroTargets structure */}
            {!drvRequirements?.macronutrients && macroTargets && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <NutrientList 
                  nutrients={Object.entries(macroTargets)}
                  emptyMessage="No macro targets available"
                  borderColor="border-cs-secondary/20"
                />
              </div>
            )}

            {/* No macros message */}
            {!hasMacros && (
              <div className="text-center py-8">
                <p className="text-gray-500">No macro targets available</p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default MacroTargetsSection;

